<script setup>
/**
 * Sharing a reading made for yourself (2026-10-19; design round 4b, frames
 * S6/S7; spec: backend docs/SHARE_PUBLIC_VERSION_SPEC.md, ids SP-…).
 *
 * One switch: "Your reading" (first, always the default) | "Its lesson".
 *
 * Your reading — who you hand it to sees it as it is:
 *   your coven (a switch since rev 13, "Your coven sees it": a flag the server
 *   keeps, so turning it off really takes it back), then one "Send it" area
 *   (2026-10-20): a row of friends' faces (one tap sends it to their Messages
 *   after a moment you can undo; search behind More), then ONE link button:
 *   "Send a link" on a phone (the share sheet inside the tap, the link made
 *   before it) or "Copy a link" on a computer. The link opens for one person
 *   (backend D14). Faces and the link stay taps: one-off sends can't be taken
 *   back, so a switch would mislead.
 *
 * Its lesson (rev 13, lesson-redo DESIGN.md §E5; contract API.md §9), top to
 *   bottom: one block, "✦ In the Community ●○" with "Shared as [name | A
 *   seeker]" on its second row (quieter while off, so the eye goes to the
 *   switch); "Here's how others will see it"; the Community's own card
 *   (LessonCard, "You asked:"), veiled while off and clear while on, so the
 *   switch and the card always agree; the Mystic's line; when on, the tally
 *   and Copy the link. The switch is `toggleShare` both ways (instant: off
 *   removes it at once). The first "on" stamps the seal and says one line.
 *   The lesson is edited on the card itself: "✎ Edit" or a tap on the lesson
 *   turns it into a field in place (Cancel · Done) with three plain choices
 *   under the Mystic's line; Done places it (`share/lesson-text/`), and a name
 *   is caught gently with a clean wording to tap. No heading, no button, no
 *   fine print.
 *
 * Not rated yet (Chris, 2026-10-29): the reading is rated right here, in one
 * quiet line with the same crystals as the end of the reading; 3 or more and
 * the line goes and the switch works, below 3 one plain line says why it
 * can't go in. A lesson that was in the Community before (`was_public`) is
 * never gated: it can always be switched back on.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { store } from '@/store'
import { getMyFriends, shareWithFriends, toggleShare } from '@/api-service'
import { handOver, prefersShareSheet } from '@/utils/care-link'
import { track, trackShare } from '@/utils/analytics'
import {
  getShareState, setCovenShare, previewPublicVersion, setLessonCredit, prepareShareLink,
  askLessonChoices, setLessonText, markRewriteSeen, isGiftRefusal,
  joinNames, initialOf, publicGate, shortDate,
} from '@/utils/share-reading'
import { mysticOf, showTally, tallyLine } from '@/utils/lesson'
import VersionSwitch from './VersionSwitch.vue'
import LessonCard from './LessonCard.vue'
import GoldSeal from './GoldSeal.vue'
import RatingCrystals from '@/components/ui/RatingCrystals.vue'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  readingId: { type: String, required: true },   // the short shared uuid
  sessionId: { type: String, default: '' },
  publicUrl: { type: String, default: '' },      // BASE/s/<short> (no token)
  shared: { type: Boolean, default: false },
  title: { type: String, default: '' },
  image: { type: String, default: '' },
  readerName: { type: String, default: '' },
  date: { type: String, default: '' },
  // Opens on this side (e.g. "Share it" after rating 3+ from the public step).
  startSide: { type: String, default: 'mine' },
})
// 'rate-request' is no longer sent (the rating is here now); kept so the page's listener stays harmless.
// 'gift': the server says this reading was made for someone (400 'gift'); the
// page opens the gift's share instead (2026-11-01, session 4771).
const emit = defineEmits(['close', 'visibility', 'rate-request', 'gift'])

const sid = computed(() => props.sessionId || store.session_id)
// A reading made for someone has no "Its lesson" here, and no fake retry.
const isGift = ref(false)
function handOverGift() {
  if (!isGift.value) emit('gift')
  isGift.value = true
  side.value = 'mine'
}
const side = ref(props.startSide === 'public' ? 'public' : 'mine')
const st = ref(null)                       // server share state
const isShared = ref(!!props.shared)       // the ONE truth for the switch and the card
const coven = computed(() => (st.value && st.value.coven) || [])
const signedIn = computed(() => !!store.querent_id || !!(st.value && st.value.signed_in))
const quietPrivate = ref(false)           // "already sent stays sent" after making it private
// Reduced motion: the state changes without the stamp or the fades (CSS does the rest).
const reduced = ref(false)

// ── the top card ─────────────────────────────────────────────────────────
const when = computed(() => shortDate(props.date) || shortDate(st.value && st.value.date) || shortDate(new Date().toISOString()))
const mysticName = computed(() => props.readerName || (st.value && st.value.reader_name) || '')
const cardTitle = computed(() => props.title || (st.value && st.value.title) || 'Your reading')
const miniSub = computed(() => ['Your reading', mysticName.value, when.value].filter(Boolean).join(' · '))
// The lesson side shows the Community's card itself, so no mini card there.
const showMini = computed(() => side.value === 'mine')
const SIDES = [{ value: 'mine', label: 'Your reading' }, { value: 'public', label: 'Its lesson' }]
const miniImgFailed = ref(false)

// ── your coven: one switch (rev 13) ───────────────────────────────────────
const covenOn = ref(false)
const covenBusy = ref(false)
watch(() => !!(st.value && st.value.coven_shared), (v) => { if (!covenBusy.value) covenOn.value = v }, { immediate: true })
const covenNames = computed(() => joinNames(coven.value.map((m) => m.name)))
async function flipCoven() {
  if (covenBusy.value) return
  const want = !covenOn.value
  covenOn.value = want                       // at once; the server's answer settles it
  covenBusy.value = true
  try {
    const r = await setCovenShare(sid.value, props.readingId, want)
    if (r) st.value = r
    covenOn.value = !!(r ? r.coven_shared : want)
    trackShare(want ? 'coven' : 'coven_stop', 'reading', props.readingId, { recipients: coven.value.length })
    track('coven_share_switch', { item_id: props.readingId, state: covenOn.value ? 'on' : 'off' })
  } catch (e) { covenOn.value = !want }
  finally { covenBusy.value = false }
}

// ── one friend ───────────────────────────────────────────────────────────
const friends = ref([])
const FACES = 4
const moreOpen = ref(false)
const friendQuery = ref('')
const faces = computed(() => friends.value.slice(0, FACES))
const searchList = computed(() => {
  const q = friendQuery.value.trim().toLowerCase()
  return friends.value.filter((f) => !q || (f.nick_name || '').toLowerCase().includes(q))
})
const sentTo = ref({})                    // uuid → true
const pending = ref(null)                 // { f, timer }
const UNDO_MS = 4000
function tapFriend(f) {
  if (!f || sentTo.value[f.querent_uuid]) return
  if (pending.value) flushPending()
  pending.value = { f, timer: setTimeout(flushPending, UNDO_MS) }
}
function undoFriend() {
  if (!pending.value) return
  clearTimeout(pending.value.timer)
  pending.value = null
}
async function flushPending() {
  const p = pending.value
  if (!p) return
  clearTimeout(p.timer)
  pending.value = null
  try {
    await shareWithFriends(props.readingId, [p.f.querent_uuid], sid.value)
    sentTo.value = { ...sentTo.value, [p.f.querent_uuid]: true }
    lastSent.value = p.f
    trackShare('friends', 'reading', props.readingId, { recipients: 1 })
  } catch (e) { friendError.value = `Couldn't send it to ${p.f.nick_name}. Try again.` }
}
const lastSent = ref(null)
const friendError = ref('')
const avatarFailed = ref({})

// ── send a link (SP-L1..L3; one person, D14) ──────────────────────────────────────────────
const phone = ref(false)
const linkPkg = ref(null)
const linkWord = ref('')                  // '' | 'Sent' | 'Link copied'
const linkFallback = ref('')              // shown only when sharing and copying both failed
async function prepareLink() {
  try {
    const r = await prepareShareLink(sid.value, props.readingId)
    if (r && r.link) linkPkg.value = { link: r.link, title: r.title, text: r.text }
  } catch (e) { /* the tap makes it then */ }
}
function onSendLink() {
  if (!linkPkg.value) return onSendLinkSlow()
  // Inside the tap: no await before the share sheet or the clipboard.
  return finishLink(handOver(linkPkg.value, { share: phone.value }))
}
async function onSendLinkSlow() {
  await prepareLink()
  if (!linkPkg.value) { linkFallback.value = props.publicUrl; return }
  return finishLink(handOver(linkPkg.value, { share: false }))
}
async function finishLink(started) {
  const how = await started
  if (how === 'cancelled') return
  if (how === 'failed') { linkFallback.value = linkPkg.value.link; return }
  linkWord.value = how === 'shared' ? 'Sent' : 'Link copied'
  trackShare(how === 'shared' ? 'share_sheet' : 'copy_link', 'reading', props.readingId)
}

// ── its lesson (L3, L4, L6; API.md §4) ────────────────────────────────────
const lesson = ref(null)                  // the server's public_version, when state is ok
const lessonState = ref('')               // '' | 'ok' | 'held' | 'failed'
const canRetry = ref(false)
const heldLine = ref('')
const writing = ref(false)
const HELD_LINE = "We couldn't write this one as a lesson without it pointing to someone, so it stays out of the Community. Your reading is still yours to send to someone you trust."
const gate = computed(() => {
  if (st.value && st.value.gate === 'low') return 'low'
  return publicGate(st.value ? st.value.rating : null, (st.value && st.value.min_rating) || 3)
})
const ready = computed(() => !!(lesson.value && lessonState.value === 'ok'))
function takeLesson(r) {
  if (!r) return
  if (r.lesson_state !== undefined) lessonState.value = r.lesson_state || ''
  if (r.can_retry !== undefined) canRetry.value = !!r.can_retry
  if (r.held_line) heldLine.value = r.held_line
  if (r.credit) credit.value = r.credit
  if (r.tally) tally.value = r.tally
  const v = r.public_version
  if (v && (v.state === 'ok' || (v.ready && !v.state))) { lesson.value = v; lessonState.value = 'ok' }
  else if (r.lesson_state && r.lesson_state !== 'ok') lesson.value = null
}
async function openPublic({ retry = false } = {}) {
  if (writing.value || isGift.value) return
  if (!retry && (ready.value || lessonState.value === 'held')) return
  if (gate.value === 'low' && !wasPublic.value) return
  writing.value = true
  try {
    const r = await previewPublicVersion(sid.value, props.readingId, retry ? { retry: true } : {})
    st.value = { ...(st.value || {}), ...r }
    takeLesson(r)
    if (!ready.value && lessonState.value !== 'held' && gate.value !== 'low') lessonState.value = 'failed'
  } catch (e) {
    if (isGiftRefusal(e)) handOverGift()
    else lessonState.value = 'failed'
  }
  finally { writing.value = false }
}
const sheetEl = ref(null)
watch(side, (s) => {
  if (s === 'public') openPublic()
  // Each side starts at its top (it kept the other side's scroll).
  if (sheetEl.value) sheetEl.value.scrollTop = 0
})

// "Shared as [ Star Gazer | A seeker ]": it changes the card's credit at once.
const credit = ref(null)                  // { choice, name, can_name, line }
const canName = computed(() => !!(credit.value && credit.value.can_name && credit.value.name))
const creditChoice = computed(() => (canName.value && credit.value.choice === 'name' ? 'name' : 'seeker'))
const creditBusy = ref(false)
function shownLesson() {
  if (!lesson.value) return null
  const named = creditChoice.value === 'name'
  // own: the sharer's card reads "You asked:" (§9.6), even before the server says so.
  return { ...lesson.value, own: true, credit_name: named ? credit.value.name : '', credit: named ? `Shared by ${credit.value.name}` : 'Shared by a seeker' }
}
const cardLesson = computed(shownLesson)
async function pickCredit(choice) {
  if (creditBusy.value || !canName.value || choice === creditChoice.value) return
  const was = credit.value
  credit.value = { ...credit.value, choice }                 // at once: no prose holds a name
  creditBusy.value = true
  try {
    const r = await setLessonCredit(sid.value, props.readingId, choice)
    if (r && r.credit) credit.value = r.credit
    if (r && r.public_version && r.public_version.state === 'ok') lesson.value = r.public_version
  } catch (e) { credit.value = was }
  finally { creditBusy.value = false }
}

// The sharer-only tally (L4): shown here and nowhere else, once either number is above zero.
const tally = ref(null)
const tallyShown = computed(() => showTally(tally.value))
const tallyParts = computed(() => tallyLine(tally.value))

// ── the switch "✦ In the Community" (E5·1, E5·3; API.md §9.1) ─────────────
// The switch flips at once and the card follows it (one ref drives both); the
// server's reply settles it, and anything but a clean reply puts both back.
const switchBusy = ref(false)
const everOn = ref(false)                 // in the Community before (this sheet or earlier)
const stamp = ref(false)                  // the seal stamps in on an "on" (not with reduced motion)
const firstLine = ref(false)              // the first time only: the one line
// In the Community before (here, or as the server remembers it): always switchable (Chris, 2026-10-29).
const wasPublic = computed(() => isShared.value || everOn.value || !!(st.value && st.value.was_public))
const canSwitch = computed(() => isShared.value || (ready.value && !writing.value && (gate.value === 'ok' || wasPublic.value)))

// ── rate it right here (Chris, 2026-10-29) ────────────────────────────────
// The same crystals as the end of the reading, in one quiet line under the
// switch. 3 or more: the line goes and the switch works. Below 3: the line
// stays (the rating can be changed) and says plainly why it can't go in.
const ratedHere = ref(false)
const rateHere = computed(() => !wasPublic.value && (gate.value === 'unrated' || (gate.value === 'low' && ratedHere.value)))
const rateLow = computed(() => ratedHere.value && gate.value === 'low')
// Rated below 3 before the sheet opened: the kind block in place of the lesson.
const lowBlock = computed(() => gate.value === 'low' && !ratedHere.value && !wasPublic.value)
function onRated(data) {
  const n = Number(data && data.rating)
  if (!n) return
  ratedHere.value = true
  st.value = { ...(st.value || {}), rating: n, gate: '' }
  if (gate.value === 'ok' && !ready.value && !writing.value) openPublic()
}
function settleOff() {
  stamp.value = false
  firstLine.value = false
}
async function flipCommunity() {
  if (switchBusy.value || !canSwitch.value) return
  const want = !isShared.value
  const back = () => { isShared.value = !want }
  // Before the flip: once the switch moves, the line's own test (on, unseen) reads false.
  const pendingRewrite = rewriteOpen.value
  isShared.value = want
  if (!want) settleOff()
  nameHit.value = null
  placedNow.value = false
  switchBusy.value = true
  try {
    const data = (await toggleShare(props.readingId))?.data
    if (!data) return back()
    if (data.reason === 'needs_resonance') { back(); st.value = { ...(st.value || {}), rating: data.rating }; return }
    if (data.reason === 'held') {
      back(); lesson.value = null; lessonState.value = 'held'; canRetry.value = !!data.can_retry; heldLine.value = data.message || HELD_LINE
      return
    }
    if (data.reason === 'lesson_failed') { back(); lesson.value = null; lessonState.value = 'failed'; return }
    if (data.reason === 'names_someone') { back(); nameHit.value = { kind: 'name', line: data.line || data.message || NAME_LINE, suggestion: data.suggestion || '' }; revealLine(); return }
    if (!data.reading_session) return back()
    isShared.value = !!data.reading_session.is_shared
    if (data.public_version) takeLesson({ public_version: data.public_version })
    if (data.tally) tally.value = data.tally
    let first = false
    if (isShared.value) {
      first = !everOn.value
      everOn.value = true
      stamp.value = !reduced.value
      firstLine.value = first
      quietPrivate.value = false
    } else {
      settleOff()
      st.value = { ...(st.value || {}), coven_shared: !!data.coven_shared, is_shared: false }
      publicCopied.value = false
      publicCopyFailed.value = false
      quietPrivate.value = true
      // Flipping off marks the after-backfill line seen on the server (§9.1):
      // it is gone for good, here too, whatever a later reply still carries.
      if (rewriteBefore.value) seeRewrite()
      if (pendingRewrite) track('lesson_rewrite_choice', { item_id: props.readingId, choice: 'private' })
    }
    emit('visibility', isShared.value)
    track('lesson_share_switch', { item_id: props.readingId, state: isShared.value ? 'on' : 'off', first })
  } catch (e) { back() }
  finally { switchBusy.value = false }
}

// Published: one button. Copy, never the share sheet (SP-P5).
const publicCopied = ref(false)
const publicCopyFailed = ref(false)
const publicLink = computed(() => props.publicUrl || ((st.value && st.value.link) || ''))
function copyPublicLink() {
  const started = handOver({ link: publicLink.value }, { share: false })
  started.then((how) => {
    if (how === 'copied') { publicCopied.value = true; trackShare('copy_link', 'reading', props.readingId, { public: true }) }
    else publicCopyFailed.value = true
  })
}

// ── the lesson, edited on the card itself (E5·2; API.md §9.3, §9.4) ───────
const NAME_LINE = 'That names someone. Strangers will read this, so let’s keep it to what you learned, not who.'
const editing = ref(false)
const draft = ref('')
const how = ref('typed')                  // 'choice' once a choice (or the clean wording) is taken
const choices = ref([])
const choicesFrom = ref('reading')        // 'reading' | 'words'
const picked = ref(-1)
const care = ref('')                      // the Mystic's care line, in place of the choices
const nameHit = ref(null)                 // { kind: 'name' | 'unkind', line, suggestion }: the field stays, amber
const placing = ref(false)
const placeError = ref('')
const updated = ref(false)                // the card glows "✦ Updated" once new words are placed
const placedNow = ref(false)              // "Here it is now. Say it differently."
const fieldEl = ref(null)
const ASK_PAUSE_MS = 800
let askTimer = null
let askSeq = 0
const star = computed(() => String((lesson.value && lesson.value.lesson) || '').trim())
const editable = computed(() => ready.value && !writing.value && (gate.value !== 'low' || wasPublic.value || ratedHere.value))
const wordCount = computed(() => draft.value.trim().split(/\s+/).filter(Boolean).length)

async function startEdit(text = null) {
  if (editing.value || !editable.value) return
  editing.value = true
  draft.value = text == null ? star.value : text
  how.value = text == null ? 'typed' : 'choice'
  picked.value = -1
  care.value = ''
  placeError.value = ''
  nameHit.value = null
  choicesFrom.value = 'reading'
  await nextTick()
  const f = fieldEl.value
  if (f) {
    f.focus()
    const end = f.value.length
    try { f.setSelectionRange(end, end) } catch (e) { /* not a text field */ }
  }
  loadChoices('')
}
async function loadChoices(text) {
  const seq = ++askSeq
  try {
    const r = await askLessonChoices(sid.value, props.readingId, text)
    if (seq !== askSeq || !editing.value || !r || r.ok === false) return    // 429: keep the last ones
    care.value = String(r.care || '')
    choices.value = care.value ? [] : (Array.isArray(r.choices) ? r.choices.filter(Boolean).slice(0, 3) : [])
    choicesFrom.value = r.from === 'words' || (r.from == null && text) ? 'words' : 'reading'
    picked.value = -1
  } catch (e) { /* no rows; the field still works */ }
}
function onInput(value) {
  draft.value = value
  how.value = 'typed'
  picked.value = -1
  nameHit.value = null
  placeError.value = ''
  clearTimeout(askTimer)
  const v = value.trim()
  if (!v || v === star.value) return
  askTimer = setTimeout(() => loadChoices(v), ASK_PAUSE_MS)
}
function takeChoice(i) {
  const c = choices.value[i]
  if (!c) return
  clearTimeout(askTimer)
  draft.value = c
  how.value = 'choice'
  picked.value = i
  nameHit.value = null
  placeError.value = ''
}
function takeFix() {
  const fix = nameHit.value && nameHit.value.suggestion
  if (!fix) return
  if (!editing.value) { startEdit(fix); return }
  clearTimeout(askTimer)
  draft.value = fix
  how.value = 'choice'
  picked.value = -1
  nameHit.value = null
}
function closeEdit() {
  clearTimeout(askTimer)
  askSeq++
  editing.value = false
  nameHit.value = null
  care.value = ''
  placeError.value = ''
}
function cancelEdit() { closeEdit() }
async function doneEdit() {
  const text = draft.value.trim()
  if (!text || placing.value) return
  clearTimeout(askTimer)
  if (text === star.value) { closeEdit(); return }
  placing.value = true
  placeError.value = ''
  try {
    const r = await setLessonText(sid.value, props.readingId, text, how.value)
    // Nothing is stored in these three; the field stays open with their words (§9.4).
    if (r && r.reason === 'names_someone') { caught({ kind: 'name', line: r.line || NAME_LINE, suggestion: String(r.suggestion || '') }); return }
    if (r && r.reason === 'unkind') { caught({ kind: 'unkind', line: r.line || UNKIND_LINE, suggestion: '' }); return }
    if (r && r.reason === 'care' && r.care) { careOnDone(String(r.care)); return }
    if (!r || r.ok === false) { placeError.value = PLACE_FAILED; return }
    st.value = { ...(st.value || {}), ...r }
    if (r.is_shared !== undefined) isShared.value = !!r.is_shared
    takeLesson(r)
    if (r.updated !== false) {
      updated.value = true
      placedNow.value = true
      seeRewrite()
      track('lesson_edit', { item_id: props.readingId, how: how.value })
    }
    closeEdit()
  } catch (e) {
    const reason = e && e.response && e.response.data && e.response.data.reason
    placeError.value = reason === 'rate_limited' ? 'That’s a lot of changes for one lesson. Give it a little while.' : PLACE_FAILED
  } finally { placing.value = false }
}
const PLACE_FAILED = 'That didn’t go through just now. Try Done again.'
const UNKIND_LINE = 'That might land hard on a stranger who comes here looking for comfort. Say what you learned a little more gently, and I’ll put it on the card.'
// A name or unkind words: the Mystic's line, an amber edge, no choices.
function caught(hit) {
  care.value = ''
  nameHit.value = hit
  revealLine()
}
// A crisis line: the care reply, as the Mystic says it in the chat, in place of
// everything under the card: no choices, no amber edge, no "try again".
function careOnDone(reply) {
  askSeq++
  nameHit.value = null
  choices.value = []
  care.value = reply
  revealLine()
}
// At 390 the card is tall: the Mystic's line that just appeared is brought into view.
const mlEl = ref(null)
async function revealLine() {
  await nextTick()
  const el = mlEl.value
  if (!el || typeof el.scrollIntoView !== 'function') return
  try { el.scrollIntoView({ block: 'nearest', behavior: reduced.value ? 'auto' : 'smooth' }) } catch (e) { /* an old browser: it stays where it is */ }
}

// ── after the backfill rewrite, once (§9.5) ───────────────────────────────
const rewriteDone = ref(false)
// Seen: gone for good in this sheet (and on the server, so after it too).
function seeRewrite() {
  rewriteDone.value = true
  if (st.value && st.value.rewrite) st.value = { ...st.value, rewrite: null }
  if (lesson.value && lesson.value.rewrite) lesson.value = { ...lesson.value, rewrite: null }
}
const rewriteBefore = computed(() => {
  const rw = (st.value && st.value.rewrite) || (lesson.value && lesson.value.rewrite) || null
  return rw && rw.before ? String(rw.before) : ''
})
const rewriteOpen = computed(() => !rewriteDone.value && isShared.value && !!rewriteBefore.value)
async function answerRewrite(choice) {
  seeRewrite()
  track('lesson_rewrite_choice', { item_id: props.readingId, choice })
  try {
    const r = await markRewriteSeen(sid.value, props.readingId, choice)
    if (r && r.ok !== false) st.value = { ...(st.value || {}), ...r }
  } catch (e) { /* it shows once more next time; nothing breaks */ }
}
function keepRewrite() { answerRewrite('keep') }
function changeRewrite() { answerRewrite('change'); startEdit() }
// "Before: “…”" with its own end mark kept inside the quotes.
const beforeEnds = computed(() => /[.!?…]$/.test(rewriteBefore.value))

// The Mystic's line under the card: which one shows.
const lineMode = computed(() => {
  if (nameHit.value) return nameHit.value.kind === 'unkind' ? 'unkind' : 'name'
  if (editing.value) return care.value ? 'care' : (choicesFrom.value === 'words' ? 'typed' : 'edit')
  if (rewriteOpen.value) return 'backfill'
  if (placedNow.value) return 'placed'
  return 'ask'
})
const lineMystic = computed(() => mysticOf(lesson.value || {}))
const porFailed = ref(false)

// ── lifecycle ───────────────────────────────────────────────────────────
onMounted(async () => {
  phone.value = prefersShareSheet()
  try { reduced.value = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) } catch (e) { reduced.value = false }
  prepareLink()
  try {
    st.value = await getShareState(sid.value, props.readingId)
    isShared.value = !!st.value.is_shared
    takeLesson(st.value)
  } catch (e) {
    st.value = st.value || null
    if (isGiftRefusal(e)) handOverGift()
  }
  everOn.value = isShared.value
  // The once-only line after the backfill: the sharer meets it on the lesson.
  if (rewriteOpen.value) side.value = 'public'
  if (side.value === 'public') openPublic()
  if (signedIn.value) {
    try {
      const data = await getMyFriends(sid.value)
      friends.value = (data && data.friends) || []
    } catch (e) { /* the row stays hidden */ }
  }
})
onBeforeUnmount(() => { clearTimeout(askTimer); if (pending.value) flushPending() })
function close() { if (pending.value) flushPending(); emit('close') }
</script>

<template>
  <Teleport to="body">
    <div class="srs-overlay" data-testid="share-reading-overlay" @click.self="close">
      <div ref="sheetEl" class="srs" :class="['side-' + side, { 'no-mini': !showMini, still: reduced }]" role="dialog" aria-modal="true" aria-label="Share this reading" data-testid="share-reading-sheet">
        <div class="srs-grab" aria-hidden="true"></div>
        <button type="button" class="srs-x" aria-label="Close" data-testid="share-close" @click="close">✕</button>

        <div v-if="showMini" class="srs-mini" data-testid="share-mini">
          <img v-if="image && !miniImgFailed" :src="image" alt="" @error="miniImgFailed = true" />
          <span v-else class="srs-mini-blank" aria-hidden="true">✦</span>
          <div class="srs-mini-words">
            <b>{{ cardTitle }}</b>
            <small>{{ miniSub }}</small>
          </div>
        </div>

        <div v-if="!isGift" class="srs-switchrow">
          <VersionSwitch v-model="side" :options="SIDES" />
        </div>

        <!-- ═══ Your reading ═══ -->
        <template v-if="side === 'mine'">
          <p class="srs-who" data-testid="share-who-line">
            <template v-if="covenOn && coven.length">You and <b>your coven</b> see your reading as it is.</template>
            <template v-else>Just you, until you share it. Whoever you share it with sees <b>your reading as it is</b>.</template>
          </p>
          <p v-if="quietPrivate" class="srs-quiet" data-testid="share-kept-line">It's private again, and its lesson has left the Community. Anything you already sent stays with the people you sent it to, like a text message.</p>

          <section v-if="signedIn && st" class="srs-coven srs-tg" :class="{ on: covenOn && coven.length }" data-testid="share-coven">
            <div v-if="coven.length" class="srs-tg-row">
              <span class="srs-tg-l">
                <span class="srs-stack" aria-hidden="true">
                  <i v-for="m in coven.slice(0, 5)" :key="m.querent_uuid">{{ initialOf(m.name) }}</i>
                </span>
                <span class="srs-tg-w"><b>Your coven sees it</b><small>{{ covenNames }}</small></span>
              </span>
              <button type="button" class="srs-sw" role="switch" :aria-checked="covenOn ? 'true' : 'false'" aria-label="Your coven sees it"
                      :aria-busy="covenBusy ? 'true' : 'false'" data-testid="share-coven-switch" @click="flipCoven"><span></span></button>
            </div>
            <template v-else>
              <div class="srs-coven-top"><b>Your coven · 0</b></div>
              <p class="srs-empty">No one in your coven yet. Add a trusted friend from the Circle tab on your Mirror to share readings like this with them.</p>
            </template>
          </section>

          <!-- One "Send it" area (2026-10-20): faces first, then one link. -->
          <section class="srs-sendit" data-testid="share-send">
            <p class="srs-lbl">Send it</p>
            <template v-if="signedIn && friends.length">
              <div class="srs-faces" data-testid="share-friends">
                <button v-for="f in faces" :key="f.querent_uuid" type="button" class="srs-face"
                        :class="{ sent: sentTo[f.querent_uuid], waiting: pending && pending.f.querent_uuid === f.querent_uuid }"
                        :aria-label="sentTo[f.querent_uuid] ? `Sent to ${f.nick_name}` : `Send it to ${f.nick_name}`"
                        data-testid="share-friend-face" @click="tapFriend(f)">
                  <i>
                    <img v-if="f.avatar_url && !avatarFailed[f.querent_uuid]" :src="f.avatar_url" alt=""
                         @error="avatarFailed = { ...avatarFailed, [f.querent_uuid]: true }" />
                    <template v-else>{{ initialOf(f.nick_name) }}</template>
                  </i>
                  <span>{{ f.nick_name }}</span>
                </button>
                <button type="button" class="srs-face more" :aria-expanded="moreOpen ? 'true' : 'false'" data-testid="share-friend-more" @click="moreOpen = !moreOpen">
                  <i aria-hidden="true"><span class="fas fa-magnifying-glass"></span></i><span>More</span>
                </button>
              </div>
              <div v-if="moreOpen" class="srs-search" data-testid="share-friend-search">
                <input v-model="friendQuery" type="search" placeholder="Search your friends" aria-label="Search your friends" />
                <ul>
                  <li v-for="f in searchList" :key="f.querent_uuid">
                    <button type="button" :disabled="sentTo[f.querent_uuid]" @click="tapFriend(f)">
                      <span class="srs-dot">{{ initialOf(f.nick_name) }}</span>{{ f.nick_name }}
                      <em v-if="sentTo[f.querent_uuid]">Sent</em>
                      <em v-else-if="f.tier === 'coven'">Coven</em>
                    </button>
                  </li>
                  <li v-if="!searchList.length" class="srs-none">No friends match “{{ friendQuery }}”.</li>
                </ul>
              </div>
              <p v-if="pending" class="srs-pending" data-testid="share-friend-pending" aria-live="polite">
                Sending it to {{ pending.f.nick_name }}… <button type="button" class="srs-link" data-testid="share-friend-undo" @click="undoFriend">Undo</button>
              </p>
              <p v-else-if="lastSent" class="srs-sent" data-testid="share-friend-sent">✓ In {{ lastSent.nick_name }}'s Messages.</p>
              <p v-else-if="friendError" class="srs-err">{{ friendError }}</p>
              <div class="srs-or" aria-hidden="true"><span>or</span></div>
            </template>

            <div class="srs-linkslot">
              <Transition name="srs-swap" mode="out-in">
                <button v-if="!linkWord" key="btn" type="button" class="srs-go outline" data-testid="share-send-link" @click="onSendLink">
                  <i :class="phone ? 'fas fa-arrow-up-from-bracket' : 'fas fa-link'" aria-hidden="true"></i>{{ phone ? 'Send a link' : 'Copy a link' }}
                </button>
                <GoldSeal v-else key="done" :label="linkWord" wide data-testid="share-link-confirm" />
              </Transition>
            </div>
            <p class="srs-caption" data-testid="share-link-caption">This link opens for one person.</p>
            <p v-if="linkFallback" class="srs-fallback" data-testid="share-link-url">{{ linkFallback }}</p>
          </section>
        </template>

        <!-- ═══ Its lesson (rev 13, §E5) ═══ -->
        <template v-else>
          <!-- L6·3: rated below 3 before the sheet opened (and never in the Community). Nothing is written. -->
          <template v-if="lowBlock">
            <h2 class="srs-h">Its lesson</h2>
            <div class="srs-kind" data-testid="share-gate-low">
              <p>This one didn't land for you, so it stays out of the Community. You can still send it to someone from Your reading.</p>
            </div>
          </template>

          <!-- L6·4: it couldn't be written without pointing to someone. -->
          <template v-else-if="lessonState === 'held' && !writing">
            <h2 class="srs-h">Its lesson</h2>
            <div class="srs-kind" data-testid="share-held">
              <p>{{ heldLine || HELD_LINE }}</p>
              <button v-if="canRetry" type="button" class="srs-link" data-testid="share-retry" @click="openPublic({ retry: true })">Try writing it once more</button>
            </div>
          </template>

          <!-- L6·5: writing failed; nothing half-written. -->
          <div v-else-if="lessonState === 'failed' && !writing" class="srs-kind" data-testid="share-write-failed">
            <p>The lesson didn't come through just now.</p>
            <button type="button" class="srs-link" data-testid="share-try-again" @click="openPublic({ retry: false })">Try again</button>
          </div>

          <template v-else>
            <!-- One block on top: the switch, and the credit on its second row. -->
            <div class="srs-tg" :class="{ on: isShared }" data-testid="share-lesson-top">
              <div class="srs-tg-row">
                <span class="srs-tg-l">
                  <i class="srs-sealmark" :class="{ stamp }" aria-hidden="true" data-testid="share-seal">✦</i>
                  <span class="srs-tg-w"><b>In the Community</b><small data-testid="share-community-sub">{{ isShared ? 'Anyone can find it' : 'Off · only you see this' }}</small></span>
                </span>
                <button type="button" class="srs-sw" role="switch" :aria-checked="isShared ? 'true' : 'false'" aria-label="In the Community"
                        :disabled="!canSwitch" :aria-busy="switchBusy ? 'true' : 'false'" data-testid="share-community-switch" @click="flipCommunity"><span></span></button>
              </div>
              <div v-if="credit" class="srs-tg-row cr" :class="{ quiet: !isShared }" data-testid="share-credit">
                <template v-if="canName">
                  <span class="srs-tg-k">Shared as</span>
                  <div class="srs-seg" role="group" aria-label="Shared as">
                    <button type="button" :class="{ on: creditChoice === 'name' }" :aria-pressed="creditChoice === 'name' ? 'true' : 'false'"
                            data-testid="credit-name" @click="pickCredit('name')">{{ credit.name }}</button>
                    <button type="button" :class="{ on: creditChoice === 'seeker' }" :aria-pressed="creditChoice === 'seeker' ? 'true' : 'false'"
                            data-testid="credit-seeker" @click="pickCredit('seeker')">A seeker</button>
                  </div>
                </template>
                <span v-else class="srs-tg-k" data-testid="credit-seeker-only">Shared as <b>a seeker</b></span>
              </div>
            </div>
            <!-- Not rated yet: rate it here, one quiet line (Chris, 2026-10-29). -->
            <div v-if="rateHere" class="srs-rate" data-testid="share-rate-here">
              <RatingCrystals :reading-id="readingId" @rated="onRated">
                <template #prompt>
                  <span v-if="rateLow" data-testid="share-rate-low">Only readings you rate 3 or more go in the Community.</span>
                  <template v-else>Rate this reading to share its lesson</template>
                </template>
              </RatingCrystals>
            </div>
            <p v-if="firstLine" class="srs-first" role="status" data-testid="share-first-line">It’s in the Community now. Flip it off any time and it leaves at once.</p>

            <p class="srs-cap" data-testid="share-cap">{{ isShared ? 'How others see it' : 'Here’s how others will see it' }}</p>

            <template v-if="writing">
              <div class="srs-shimmer" data-testid="share-public-writing" aria-hidden="true">
                <i class="p"></i><i class="l"></i><i class="l w"></i><i class="l s"></i>
              </div>
              <p class="srs-writing" aria-live="polite">Drawing out its lesson…</p>
            </template>
            <template v-else-if="cardLesson">
              <LessonCard :lesson="cardLesson" :preview="!isShared" wide :look="isShared ? 'clear' : 'veiled'"
                          :editable="editable" :editing="editing" :updated="updated" @edit="startEdit()">
                <template #edit>
                  <textarea ref="fieldEl" class="srs-field" :class="{ named: !!nameHit }" :value="draft" rows="3" maxlength="160"
                            aria-label="The lesson, in your words" data-testid="lesson-card-field"
                            @input="onInput($event.target.value)" @keydown.enter.prevent="doneEdit" @keydown.esc.stop="cancelEdit"></textarea>
                  <div class="srs-edbar">
                    <span v-if="wordCount > 12" class="srs-count" data-testid="lesson-edit-count">{{ wordCount }} words · shorter lands better</span>
                    <button type="button" class="srs-ed-cancel" data-testid="lesson-edit-cancel" @click="cancelEdit">Cancel</button>
                    <button type="button" class="srs-ed-done" :disabled="!draft.trim() || placing" :aria-busy="placing ? 'true' : 'false'"
                            data-testid="lesson-edit-done" @click="doneEdit">Done</button>
                  </div>
                </template>
              </LessonCard>

              <!-- The Mystic's line: a square portrait, one sentence; replies are inline words, never chips. -->
              <div ref="mlEl" class="srs-ml" :class="{ care: lineMode === 'care' }" aria-live="polite">
                <div class="srs-ml-row">
                  <span class="srs-ml-por">
                    <img v-if="lineMystic.avatar && !porFailed" :src="lineMystic.avatar" :alt="lineMystic.name" @error="porFailed = true" />
                    <span v-else :aria-label="lineMystic.name">{{ initialOf(lineMystic.name) }}</span>
                  </span>
                  <p class="srs-ml-t" data-testid="share-mystic-line">
                    <template v-if="lineMode === 'name' && nameHit.suggestion">That names someone, and strangers will read this. Keep it to what you learned: <a href="#" role="button" class="srs-ml-link" data-testid="share-name-fix" @click.prevent="takeFix" @keydown.space.prevent="takeFix">“{{ nameHit.suggestion }}”</a></template>
                    <template v-else-if="lineMode === 'name' || lineMode === 'unkind'">{{ nameHit.line }}</template>
                    <template v-else-if="lineMode === 'care'">{{ care }}</template>
                    <template v-else-if="lineMode === 'typed'">Here’s your thought, tidied for strangers. Take one, or keep your own:</template>
                    <template v-else-if="lineMode === 'edit'">Change it right on the card, or take one of these:</template>
                    <template v-else-if="lineMode === 'backfill'">We reworded your lesson so a stranger can follow it. It used to say: <i>“{{ rewriteBefore }}”</i>{{ beforeEnds ? '' : '.' }} <span class="srs-ml-acts"><a href="#" role="button" class="srs-ml-link" data-testid="share-rewrite-keep" @click.prevent="keepRewrite" @keydown.space.prevent="keepRewrite">Keep the new one</a> · <a href="#" role="button" class="srs-ml-link" data-testid="share-rewrite-change" @click.prevent="changeRewrite" @keydown.space.prevent="changeRewrite">Change it</a></span></template>
                    <template v-else-if="lineMode === 'placed'">Here it is now. <a href="#" role="button" class="srs-ml-link" data-testid="share-say-again" @click.prevent="startEdit()" @keydown.space.prevent="startEdit()">Say it differently</a>.</template>
                    <template v-else>Is this what you took from it?</template>
                  </p>
                </div>
                <div v-if="editing && !nameHit && !care && choices.length" class="srs-ch" role="group" aria-label="Wordings">
                  <button v-for="(c, i) in choices" :key="c" type="button" class="srs-ch-o" :class="{ picked: picked === i }"
                          data-testid="share-choice" @click="takeChoice(i)"><span aria-hidden="true">✦</span>{{ c }}</button>
                </div>
                <p v-if="placeError" class="srs-ml-err" role="status" data-testid="share-place-error">{{ placeError }}</p>
              </div>
            </template>

            <template v-if="isShared && !editing && cardLesson">
              <p v-if="tallyShown" class="srs-stats" data-testid="share-tally">
                <template v-for="(t, i) in tallyParts" :key="i"><template v-if="i"> · </template><b>{{ t.n }}</b> {{ t.words }}</template>
              </p>
              <div class="srs-copyslot">
                <Transition name="srs-swap" mode="out-in">
                  <button v-if="!publicCopied" key="copy" type="button" class="srs-go outline" data-testid="share-copy-public" @click="copyPublicLink">Copy the link</button>
                  <GoldSeal v-else key="copied" label="Link copied" wide data-testid="share-public-copied" />
                </Transition>
              </div>
              <p v-if="publicCopyFailed" class="srs-fallback" data-testid="share-public-url">{{ publicLink }}</p>
            </template>
          </template>
        </template>
        <!-- Room below the last line, the phone's safe area included (a spacer: scroll containers can drop bottom padding). -->
        <div class="srs-end" aria-hidden="true" data-testid="share-sheet-end"></div>
      </div>
    </div>
  </Teleport>
</template>
<style scoped>
.srs-overlay {
  position: fixed; inset: 0; z-index: 1200; display: flex; align-items: flex-end; justify-content: center;
  background: rgba(5, 2, 12, .62); overscroll-behavior: contain;
}
.srs {
  --cream: #f4ecdc; --cream2: #fbf6ec; --paper-edge: #e4d6b8; --gold: #c9a94e; --goldlt: #e7c983; --golddk: #8a6a2a;
  --ink: #2a1845; --ink2: #4a3a63; --mute: #6b5a82; --amethyst: #4e2776; --violet: #7b4bb0;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  position: relative; box-sizing: border-box; width: 100%; height: calc(100dvh - 56px); overflow-y: auto; overflow-x: hidden;
  display: flex; flex-direction: column;
  background: var(--cream2); color: var(--ink); border-radius: 26px 26px 0 0;
  padding: 14px 20px 0; font-family: var(--ui);
  box-shadow: 0 -20px 60px -20px rgba(0, 0, 0, .6); animation: srs-up .26s cubic-bezier(.2, .8, .2, 1) both;
}
@keyframes srs-up { from { transform: translateY(24px); opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .srs { animation: none; } }
.srs *, .srs *::before, .srs *::after { box-sizing: border-box; }
.srs > * { flex-shrink: 0; }
.srs-grab { width: 40px; height: 5px; border-radius: 3px; background: #d9cbb0; margin: 0 auto 10px; }
.srs-x {
  position: absolute; top: 10px; right: 12px; z-index: 3; width: 44px; height: 44px; border-radius: 50%; border: 0;
  background: #efe5d2; color: var(--amethyst); font: 600 15px var(--ui); cursor: pointer; display: grid; place-items: center;
}
.srs-x:focus-visible, .srs-seg button:focus-visible, .srs-go:focus-visible, .srs-link:focus-visible, .srs-face:focus-visible { outline: 2px solid var(--violet); outline-offset: 2px; }

.srs-mini { display: grid; grid-template-columns: 84px minmax(0, 1fr); gap: 12px; align-items: center; margin: 34px 0 14px; padding: 10px; border-radius: 14px; background: #fff; border: 1px solid var(--paper-edge); }
.srs-mini img, .srs-mini-blank { width: 84px; height: 60px; object-fit: cover; border-radius: 8px; display: grid; place-items: center; background: var(--ink); color: var(--goldlt); }
.srs-mini-words { min-width: 0; }
.srs-mini b { display: block; font: 600 15px/1.25 var(--display); color: var(--ink); overflow-wrap: anywhere; }
.srs-mini small { display: block; margin-top: 3px; font: 500 12px/1.4 var(--ui); color: #8a7a9f; overflow-wrap: anywhere; }

.srs-switchrow { display: flex; align-items: center; min-height: 44px; margin: 0 52px 8px 0; }
.no-mini .srs-switchrow { margin-top: 0; }

.srs-who { margin: 4px 0 12px; font: 500 12.5px/1.45 var(--ui); color: #5d4d78; text-align: center; overflow-wrap: anywhere; }
.srs-who b { color: var(--ink); }
.srs-quiet { margin: -4px 0 12px; padding: 10px 12px; border-radius: 12px; background: #f3ecfb; font: 500 12.5px/1.45 var(--ui); color: #5d4d78; }

.srs-coven { padding: 14px; border-radius: 16px; background: #fff; border: 1px solid var(--paper-edge); margin: 0 0 14px; }
.srs-coven-top { display: flex; align-items: center; gap: 10px; margin: 0 0 6px; min-width: 0; }
.srs-coven-top b { font: 600 15px var(--ui); color: var(--ink); }
.srs-stack { display: flex; flex: 0 0 auto; }
.srs-stack i { width: 28px; height: 28px; border-radius: 50%; margin-left: -8px; border: 2px solid #fff; display: grid; place-items: center; font: 600 11px var(--display); font-style: normal; color: #fff; background: linear-gradient(135deg, #7b4bb0, #c9a94e); }
.srs-stack i:first-child { margin-left: 0; }
.srs-coven p { margin: 0 0 10px; font: 500 12.5px/1.45 var(--ui); color: var(--mute); overflow-wrap: anywhere; }
.srs-coven .srs-empty { margin: 0; }
.srs-coven-done { display: flex; flex-direction: column; align-items: center; gap: 2px; }

.srs-go {
  display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; min-height: 52px; padding: 10px 18px;
  border-radius: 999px; border: 1px solid rgba(240, 217, 163, .55); background: linear-gradient(180deg, #7b4bb0, #4e2776);
  color: #ffdfa3; font: 700 15px/1.2 var(--ui); letter-spacing: .02em; cursor: pointer; box-shadow: 0 14px 30px -12px rgba(78, 39, 118, .7);
}
.srs-coven .srs-go { min-height: 48px; font-size: 14px; }
.srs-go.outline { background: none; color: var(--amethyst); border: 1.5px solid #cdb8ea; box-shadow: none; min-height: 48px; font-size: 14px; }
.srs-go.dim, .srs-go:disabled { opacity: .55; cursor: default; }
.srs-go.publish { margin-top: 4px; min-height: 56px; }

.srs-lbl { margin: 0 0 8px; font: 600 11px/1.4 var(--display); letter-spacing: .16em; text-transform: uppercase; color: var(--golddk); }
.srs-faces { display: flex; gap: 8px; margin: 0 0 8px; min-width: 0; }
.srs-face {
  flex: 0 1 62px; min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 2px 0; border: 0; background: none; cursor: pointer;
  font: 500 11.5px/1.2 var(--ui); color: var(--ink2);
}
.srs-face i {
  position: relative; width: 52px; height: 52px; border-radius: 50%; display: grid; place-items: center; overflow: visible;
  font: 600 18px var(--display); font-style: normal; color: #fff; background: linear-gradient(135deg, #7b4bb0, #c9a94e);
}
.srs-face i img { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; display: block; }
.srs-face > span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.srs-face.more i { background: #efe5fb; color: var(--amethyst); font-size: 17px; }
.srs-face.waiting i { box-shadow: 0 0 0 3px var(--goldlt); }
.srs-face.sent i::after {
  content: '✓'; position: absolute; right: -4px; bottom: -2px; width: 22px; height: 22px; border-radius: 50%; display: grid; place-items: center;
  font: 700 12px var(--ui); color: #5a3f0f; background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 45%, #8a6a2a 100%);
}
.srs-search { margin: 0 0 8px; padding: 10px; border-radius: 14px; background: #fff; border: 1px solid var(--paper-edge); }
.srs-search input { width: 100%; min-height: 44px; padding: 10px 12px; border: 1.5px solid #d9c9ef; border-radius: 12px; font: 500 16px var(--ui); color: var(--ink); background: #fff; }
.srs-search ul { list-style: none; margin: 6px 0 0; padding: 0; max-height: 220px; overflow-y: auto; }
.srs-search li button { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 44px; padding: 6px 4px; border: 0; background: none; font: 500 14px var(--ui); color: var(--ink); cursor: pointer; text-align: left; }
.srs-search li em { margin-left: auto; font-style: normal; font-size: 12px; color: var(--golddk); }
.srs-dot { width: 30px; height: 30px; border-radius: 50%; display: grid; place-items: center; color: #fff; font: 600 12px var(--display); background: linear-gradient(135deg, #7b4bb0, #c9a94e); flex: 0 0 auto; }
.srs-none { padding: 8px 4px; font: 500 13px var(--ui); color: var(--mute); }

.srs-pending, .srs-sent { margin: 0 0 12px; font: 500 13px/1.45 var(--ui); color: var(--ink2); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.srs-sent { color: #2f6b3a; }
.srs-err { margin: 6px 0 12px; font: 500 13px/1.45 var(--ui); color: #a3402d; }

.srs-link {
  display: inline-flex; align-items: center; min-height: 44px; padding: 0 6px; border: 0; background: none; cursor: pointer;
  font: 600 13.5px var(--ui); color: var(--amethyst); text-decoration: underline; text-underline-offset: 3px;
}
.srs-link:disabled { opacity: .5; cursor: default; }

/* One "Send it" area (2026-10-20): faces, a quiet "or", one link button. */
.srs-sendit { padding: 14px 14px 12px; border-radius: 16px; background: #fff; border: 1px solid var(--paper-edge); margin: 0 0 6px; }
.srs-sendit .srs-lbl { margin-bottom: 10px; }
.srs-sendit .srs-faces { justify-content: center; }
.srs-or { display: flex; align-items: center; gap: 10px; margin: 4px 0 12px; font: 500 11px/1 var(--ui); letter-spacing: .14em; text-transform: uppercase; color: #a596b8; }
.srs-or::before, .srs-or::after { content: ''; flex: 1; border-top: 1px solid #efe6d6; }
.srs-linkslot { min-height: 48px; display: flex; flex-direction: column; justify-content: center; }
.srs-caption { margin: 8px 0 0; text-align: center; font: 500 12.5px/1.45 var(--ui); color: var(--mute); }
.srs-fallback { margin: 8px 0 0; padding: 8px 10px; border-radius: 10px; background: #fff; border: 1px dashed #cdb8ea; font: 500 12.5px/1.4 var(--ui); color: var(--ink); overflow-wrap: anywhere; user-select: all; }
.srs-send { flex: 0 0 auto; min-height: 44px; padding: 0 20px; border-radius: 999px; border: 0; background: var(--amethyst); color: #fff; font: 600 14px var(--ui); cursor: pointer; }
.srs-send:disabled { opacity: .45; cursor: default; }

/* ── its lesson (L3, L4, L6) ─────────────────────────────────── */
.side-public .srs-switchrow { margin-bottom: 18px; }
.srs-h { margin: 0 0 6px; font: 700 25px/1.15 var(--serif); color: var(--ink); overflow-wrap: anywhere; }
.srs-sub { margin: 0 0 16px; font: 500 14px/1.5 var(--ui); color: #5d4d78; }
.srs-kind { margin: 0 0 14px; padding: 16px; border-radius: 14px; background: #f3ead8; border: 1px solid var(--paper-edge); }
.srs-kind p { margin: 0; font: 500 17px/1.45 var(--serif); color: var(--ink); overflow-wrap: anywhere; }
.srs-kind .srs-link { padding-left: 0; margin-top: 2px; }
.srs-shimmer { position: relative; display: block; border-radius: 18px; overflow: hidden; border: 1px solid var(--paper-edge); background: var(--cream); padding-bottom: 18px; }
.srs-shimmer i { display: block; }
.srs-shimmer .p { height: 150px; background: linear-gradient(100deg, #e9dcc2 20%, #f6efe0 40%, #e9dcc2 60%); background-size: 220% 100%; animation: srs-sh 1.6s linear infinite; }
.srs-shimmer .l { height: 12px; margin: 14px 16px 0; border-radius: 6px; background: #e9dcc2; }
.srs-shimmer .l.w { width: 70%; }
.srs-shimmer .l.s { width: 40%; }
@keyframes srs-sh { from { background-position: 120% 0; } to { background-position: -120% 0; } }
@media (prefers-reduced-motion: reduce) { .srs-shimmer .p { animation: none; } }
.srs-writing { margin: 12px 0 16px; text-align: center; font: italic 500 17px var(--serif); color: #5d4d78; }
.srs-credit { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin: 16px 0 18px; font: 500 13.5px var(--ui); color: #5d4d78; min-width: 0; }
.srs-credit b { color: var(--ink); }
.srs-seg { display: inline-flex; padding: 3px; border-radius: 999px; border: 1px solid var(--paper-edge); background: #fff; min-width: 0; max-width: 100%; }
.srs-seg button {
  min-height: 44px; min-width: 44px; padding: 0 16px; border: 0; border-radius: 999px; background: none; cursor: pointer;
  font: 600 13px var(--ui); color: #6b5a82; min-width: 0; overflow-wrap: anywhere;
}
.srs-seg button.on { background: var(--amethyst); color: #ffdfa3; }
.srs-go.publish { margin-top: 18px; min-height: 56px; }
.srs-credit + .srs-go.publish { margin-top: 0; }
.srs-fine.center { margin: 12px 0 0; padding-top: 0; }
.srs-seal { display: flex; flex-direction: column; align-items: center; gap: 8px; margin: 4px 0 16px; text-align: center; }
.srs-sealmark {
  width: 54px; height: 54px; border-radius: 50%; display: grid; place-items: center; font: 700 20px var(--display); font-style: normal; color: #5a3f0f;
  background: radial-gradient(circle at 35% 30%, #f3d98f, #c9a94e 45%, #8a6a2a 100%);
  box-shadow: 0 4px 12px rgba(42, 24, 69, .4), inset 0 0 0 4px rgba(255, 240, 200, .35);
  animation: srs-press .42s cubic-bezier(.2, .9, .3, 1.3) both;
}
@keyframes srs-press { 0% { transform: scale(1.5) rotate(-18deg); opacity: 0; } 100% { transform: none; opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .srs-sealmark { animation: none; } }
.srs-seal b { font: 700 22px var(--serif); color: var(--ink); }
.srs-seal span { font: 500 13.5px/1.45 var(--ui); color: #5d4d78; }
.srs-stats { margin: 14px 0 0; padding: 12px 14px; border-radius: 14px; background: #f3ead8; font: 500 13.5px/1.5 var(--ui); color: #4a3a63; text-align: center; overflow-wrap: anywhere; }
.srs-stats b { color: var(--amethyst); }
.srs-copyslot { min-height: 56px; display: flex; flex-direction: column; justify-content: center; }
.srs-published { display: flex; flex-direction: column; align-items: center; gap: 18px; margin: 6px 0 0; }
.srs-fine { margin: auto 0 0; padding-top: 12px; text-align: center; font: 500 12.5px/1.55 var(--ui); color: #8a7a9f; }
.srs-fine .srs-link { font-size: 12.5px; }

/* ── rev 13 (§E5): one block on top, the switch and the credit ─────── */
.srs-tg { margin: 2px 0 12px; border-radius: 16px; background: #fff; border: 1px solid var(--paper-edge); overflow: hidden; transition: background .4s, border-color .4s; }
.srs-tg.on { background: #fff7e3; border-color: var(--goldlt); }
.srs-tg-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px 10px 14px; min-width: 0; }
.srs-tg-row.cr { border-top: 1px solid #efe6d6; padding-top: 8px; padding-bottom: 8px; transition: opacity .4s; }
/* Tweak 1: "Shared as" shows in both states, quieter while off, so the eye goes to the switch. */
.srs-tg-row.cr.quiet .srs-tg-k { color: #a094b3; }
.srs-tg-row.cr.quiet .srs-seg { border-color: #efe6d6; }
.srs-tg-row.cr.quiet .srs-seg button { color: #a094b3; }
.srs-tg-row.cr.quiet .srs-seg button.on { background: #ebe2f5; color: #6b5a82; }
.srs-tg-l { display: flex; align-items: center; gap: 12px; min-width: 0; }
.srs-tg-w { min-width: 0; }
.srs-tg-w b { display: block; font: 700 19px/1.2 var(--serif); color: var(--ink); }
.srs-tg-w small { display: block; font: 500 12.5px/1.35 var(--ui); color: var(--mute); overflow-wrap: anywhere; }
.srs-tg-k { font: 500 13.5px var(--ui); color: #5d4d78; transition: color .4s; }
.srs-tg-k b { color: inherit; }
.srs-tg .srs-seg button { min-height: 40px; }
@media (max-width: 767px) { .srs-tg .srs-seg button { min-height: 44px; } }
.srs-tg .srs-seg button { transition: background .3s, color .3s; }
.srs-tg .srs-sealmark { width: 34px; height: 34px; font-size: 16px; flex: none; animation: none; transition: filter .4s, opacity .4s; }
.srs-tg:not(.on) .srs-sealmark { filter: grayscale(1); opacity: .45; }
.srs-tg .srs-sealmark.stamp { animation: srs-stamp .55s cubic-bezier(.2, .9, .3, 1.3) both; }
@keyframes srs-stamp { 0% { transform: scale(1.7) rotate(-20deg); opacity: 0; } 100% { transform: none; opacity: 1; } }
/* The switch (both tabs). */
.srs-sw { position: relative; flex: none; width: 56px; height: 44px; border: 0; padding: 0; cursor: pointer; background: none; border-radius: 22px; }
/* The track (52x32) inside a 44px tap target. */
.srs-sw::before { content: ''; position: absolute; left: 2px; top: 6px; width: 52px; height: 32px; border-radius: 16px; background: #cbbfd9; transition: background .2s; }
.srs-sw span { position: absolute; top: 9px; left: 5px; width: 26px; height: 26px; border-radius: 50%; background: #fff; box-shadow: 0 2px 6px rgba(42, 24, 69, .35); transition: transform .2s; }
.srs-sw[aria-checked="true"]::before { background: linear-gradient(180deg, #7b4bb0, #4e2776); }
.srs-sw[aria-checked="true"] span { transform: translateX(20px); }
.srs-sw:disabled { opacity: .45; cursor: default; }
.srs-sw:focus-visible { outline: 2px solid var(--violet); outline-offset: 1px; }
/* The coven as a switch: the same block. */
.srs-coven.srs-tg { padding: 0; margin: 0 0 14px; }
.srs-coven.srs-tg > .srs-coven-top, .srs-coven.srs-tg > .srs-empty { padding: 0 14px; }
.srs-coven.srs-tg > .srs-coven-top { padding-top: 14px; }
.srs-coven.srs-tg > .srs-empty { padding-bottom: 14px; }
.srs-coven .srs-tg-w b { font: 600 15px/1.25 var(--ui); }
.srs-coven .srs-tg-w small { margin: 2px 0 0; }

.srs-first { margin: -4px 0 12px; text-align: center; font: italic 500 15px/1.4 var(--serif); color: var(--golddk); animation: srs-rise .5s ease .2s both; }
@keyframes srs-rise { from { opacity: 0; transform: translateY(10px); } }
.srs-cap { margin: 4px 0 8px; font: 600 10.5px var(--display); letter-spacing: .18em; text-transform: uppercase; color: var(--golddk); text-align: center; }

/* The field on the card itself (slot content in LessonCard). */
.srs-field {
  display: block; width: 100%; resize: none; border: 0; border-bottom: 2px solid var(--gold); border-radius: 8px 8px 0 0;
  background: rgba(255, 255, 255, .6); padding: 6px 8px; font: italic 600 25px/1.18 var(--serif); color: var(--ink); text-align: center; overflow: auto;
}
.srs-field:focus { outline: none; background: #fff; box-shadow: 0 0 0 3px rgba(201, 169, 78, .25); }
/* A name: a soft amber edge, no error colour. */
.srs-field.named { border-bottom-color: #d99a3a; box-shadow: 0 0 0 3px rgba(217, 154, 58, .22); }
.srs-edbar { display: flex; align-items: center; justify-content: flex-end; gap: 6px; margin: 8px 0 2px; flex-wrap: wrap; }
.srs-count { margin-right: auto; font: 600 11.5px var(--ui); color: #b0782a; }
.srs-ed-done, .srs-ed-cancel { min-height: 44px; padding: 0 16px; border-radius: 999px; font: 600 13.5px var(--ui); cursor: pointer; }
.srs-ed-done { border: 1px solid rgba(240, 217, 163, .55); background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #ffdfa3; }
.srs-ed-done:disabled { opacity: .5; cursor: default; }
.srs-ed-cancel { border: 0; background: none; color: var(--mute); }
.srs-ed-done:focus-visible, .srs-ed-cancel:focus-visible { outline: 2px solid var(--violet); outline-offset: 2px; }

/* The Mystic's line under the card: a square portrait, one sentence. */
.srs-ml { margin: 14px 0 2px; scroll-margin: 16px 0 28px; }
.srs-ml-row { display: grid; grid-template-columns: 34px minmax(0, 1fr); gap: 12px; align-items: start; }
.srs-ml-por { width: 34px; height: 34px; border-radius: 8px; overflow: hidden; border: 1px solid var(--gold); box-shadow: 0 6px 12px -8px rgba(42, 24, 69, .6);
  display: grid; place-items: center; background: var(--ink); color: var(--goldlt); font: 600 14px var(--display); }
.srs-ml-por img { width: 100%; height: 100%; object-fit: cover; display: block; }
.srs-ml-t { margin: 0; padding-left: 12px; border-left: 2px solid rgba(201, 169, 78, .55); font: 500 17px/1.5 var(--serif); color: var(--ink); overflow-wrap: anywhere; }
.srs-ml-t i { color: var(--mute); }
/* Inline replies in the sentence; the vertical padding makes a 44px tap without moving the line. */
.srs-ml-link {
  display: inline; padding: 12px 0; cursor: pointer; color: var(--amethyst);
  text-decoration: underline; text-decoration-color: rgba(78, 39, 118, .45); text-underline-offset: 3px; border-radius: 3px;
  -webkit-box-decoration-break: clone; box-decoration-break: clone;
}
.srs-ml-link:hover { text-decoration-color: currentColor; }
.srs-ml-link:focus-visible { outline: 2px solid var(--violet); outline-offset: 2px; }
.srs-ml-err { margin: 8px 0 0 46px; font: 500 13px/1.45 var(--ui); color: #8a5a1a; }
/* Three plain choices: bordered rows with a ✦, not chips, not chat. */
.srs-ch { display: grid; gap: 8px; margin: 10px 0 4px 46px; }
.srs-ch-o {
  display: flex; align-items: flex-start; gap: 10px; width: 100%; min-height: 48px; padding: 11px 14px; border-radius: 14px; border: 1px solid var(--paper-edge);
  background: #fff; cursor: pointer; text-align: left; font: italic 500 17px/1.3 var(--serif); color: var(--ink); overflow-wrap: anywhere;
}
.srs-ch-o span { color: var(--gold); font-style: normal; font-size: 13px; line-height: 1.7; }
.srs-ch-o:hover, .srs-ch-o:focus-visible { border-color: var(--gold); outline: none; box-shadow: 0 6px 14px -10px rgba(42, 24, 69, .5); }
.srs-ch-o.picked { border-color: var(--gold); background: #fff7e3; }
@media (max-width: 480px) { .srs-ch { margin-left: 0; } }
.side-public .srs-copyslot { margin-top: 12px; }

/* The rating, here (Chris, 2026-10-29): one quiet line, the end-of-reading crystals. */
.srs-rate { margin: -4px 0 12px; }
.srs-rate :deep(.rc) { margin: 0; padding: 0; background: none; border: 0; animation: none; }
.srs-rate :deep(.rc-row) { justify-content: center; gap: 2px 8px; }
.srs-rate :deep(.rc-prompt) { font: 500 13.5px/1.35 var(--ui); color: #5d4d78; text-align: center; }
.srs-rate :deep(.rc-ball) { min-width: 40px; min-height: 40px; font-size: 20px; }
@media (max-width: 767px) { .srs-rate :deep(.rc-ball) { min-width: 44px; min-height: 44px; } }
.srs-rate :deep(.rc-label), .srs-rate :deep(.rc-confirm) { display: none; }
/* "Keep the new one · Change it" on their own line under the old wording. */
.srs-ml-acts { display: block; margin-top: 2px; }
/* The sheet's last thing: room to breathe, the safe area on top. */
.srs-end { flex: 0 0 auto; height: calc(32px + env(safe-area-inset-bottom, 0px)); }

/* Reduced motion: the state changes without the stamp or the fades. */
@media (prefers-reduced-motion: reduce) {
  .srs-tg, .srs-tg-row.cr, .srs-tg-k, .srs-tg .srs-seg button, .srs-tg .srs-sealmark, .srs-sw::before, .srs-sw span { transition: none; }
  .srs-tg .srs-sealmark.stamp, .srs-first { animation: none; }
}
.srs.still .srs-tg .srs-sealmark.stamp, .srs.still .srs-first { animation: none; }

.srs-swap-enter-active, .srs-swap-leave-active { transition: opacity .18s ease, transform .18s ease; }
.srs-swap-enter-from { opacity: 0; transform: scale(.97); }
.srs-swap-leave-to { opacity: 0; }

@media (min-width: 768px) {
  .srs-overlay { align-items: center; padding: 24px; }
  .srs { max-width: 480px; height: auto; max-height: min(88dvh, 860px); border-radius: 22px; padding: 18px 24px 0; box-shadow: 0 30px 80px -20px rgba(0, 0, 0, .7); }
  .srs-end { height: 32px; }
  .srs-grab { display: none; }
  .srs-mini { margin-top: 30px; }
}
</style>
